import React from 'react';
import { motion } from 'framer-motion';
import { Trash2, RefreshCw, Move, ZoomIn, ZoomOut } from 'lucide-react';
import { toast } from 'sonner';

export const EditorControls = ({ engine, availablePhotos }) => {
  const {
    elements, selectedGridSlot, scale, hoveredOverlay,
    handleGridDeleteImage, handleGridSlotSelect, handleGridZoomChange,
    updateElement, deleteElement, setSelectedId,
    setPhotoPickerTarget, setShowPhotoPicker, fileInputRef,
    setHoveredOverlay
  } = engine;

  // Helper: is this grid slot hovered?
  const isGridSlotHovered = (gridId, slotIndex) =>
    hoveredOverlay?.type === 'gridSlot' && hoveredOverlay.gridId === gridId && hoveredOverlay.slotIndex === slotIndex;

  // Helper: is this image hovered?
  const isImageHovered = (imgId) =>
    hoveredOverlay?.type === 'image' && hoveredOverlay.id === imgId;

  return (
    <>
      {/* Grid Slot Overlays — container is pointer-events:none so clicks reach Konva */}
      {elements.filter(el => el.type === 'photoGrid').map(grid =>
        grid.slots.map((slot, i) => {
          if (!slot.src) return null;
          const show = isGridSlotHovered(grid.id, i);
          return (
            <div
              key={`go-${grid.id}-${i}`}
              className="absolute pointer-events-none"
              style={{
                left: slot.x * scale, top: slot.y * scale,
                width: slot.width * scale, height: slot.height * scale,
                opacity: show ? 1 : 0,
                transition: 'opacity 0.15s ease',
              }}
            >
              <div className="absolute inset-0 bg-black/40 flex items-center justify-center gap-3">
                <motion.button
                  onClick={(e) => {
                    e.stopPropagation();
                    setHoveredOverlay(null);
                    handleGridDeleteImage(grid.id, i);
                  }}
                  className="bg-red-500 hover:bg-red-600 text-white rounded-full p-2 shadow-lg pointer-events-auto"
                  whileHover={{ scale: 1.1 }} whileTap={{ scale: 0.95 }}
                  title="Delete photo" data-testid={`delete-grid-photo-${grid.id}-${i}`}>
                  <Trash2 className="h-5 w-5" />
                </motion.button>
                <motion.button
                  onClick={(e) => {
                    e.stopPropagation();
                    setHoveredOverlay(null);
                    if (availablePhotos.length > 0) {
                      setPhotoPickerTarget({ type: 'grid', gridId: grid.id, slotIndex: i });
                      setShowPhotoPicker(true);
                    } else { fileInputRef.current?.click(); }
                  }}
                  className="bg-blue-500 hover:bg-blue-600 text-white rounded-full p-2 shadow-lg pointer-events-auto"
                  whileHover={{ scale: 1.1 }} whileTap={{ scale: 0.95 }}
                  title="Replace photo" data-testid={`replace-grid-photo-${grid.id}-${i}`}>
                  <RefreshCw className="h-5 w-5" />
                </motion.button>
                <motion.button
                  onClick={(e) => {
                    e.stopPropagation();
                    setHoveredOverlay(null);
                    handleGridSlotSelect(grid.id, i);
                    toast.info('Drag the image to reposition. Use the zoom slider below.');
                  }}
                  className="bg-purple-500 hover:bg-purple-600 text-white rounded-full p-2 shadow-lg pointer-events-auto"
                  whileHover={{ scale: 1.1 }} whileTap={{ scale: 0.95 }}
                  title="Adjust position" data-testid={`adjust-grid-photo-${grid.id}-${i}`}>
                  <Move className="h-5 w-5" />
                </motion.button>
              </div>
            </div>
          );
        })
      )}

      {/* Floating Zoom Slider for selected grid slot */}
      {selectedGridSlot && (() => {
        const grid = elements.find(e => e.id === selectedGridSlot.gridId);
        const slot = grid?.slots?.[selectedGridSlot.slotIndex];
        if (!grid || !slot?.src) return null;
        return (
          <div
            className="absolute z-10 bg-white/95 backdrop-blur-sm rounded-lg shadow-lg px-3 py-2 flex items-center gap-2"
            style={{
              left: slot.x * scale,
              top: (slot.y + slot.height) * scale + 8,
              width: Math.max(160, slot.width * scale),
            }}
            data-testid="floating-zoom-slider"
          >
            <ZoomOut className="h-3.5 w-3.5 text-gray-500 flex-shrink-0" />
            <input
              type="range" min="100" max="300" step="5"
              value={Math.round((slot.imageZoom || 1) * 100)}
              onChange={(e) => handleGridZoomChange(grid.id, selectedGridSlot.slotIndex, parseInt(e.target.value) / 100)}
              className="flex-1 h-1.5 accent-purple-600"
              data-testid="zoom-range-input"
            />
            <ZoomIn className="h-3.5 w-3.5 text-gray-500 flex-shrink-0" />
            <span className="text-xs text-gray-600 w-10 text-right font-medium">
              {Math.round((slot.imageZoom || 1) * 100)}%
            </span>
          </div>
        );
      })()}

      {/* Image Element Overlays — container pointer-events:none, only buttons catch clicks */}
      {elements.filter(el => el.type === 'image' && el.src).map(imgEl => {
        const show = isImageHovered(imgEl.id);
        return (
          <div
            key={`io-${imgEl.id}`}
            className="absolute pointer-events-none"
            style={{
              left: imgEl.x * scale, top: imgEl.y * scale,
              width: imgEl.width * scale, height: imgEl.height * scale,
              opacity: show ? 1 : 0,
              transition: 'opacity 0.15s ease',
            }}
          >
            <div className="absolute inset-0 bg-black/40 flex items-center justify-center gap-3 rounded">
              <motion.button
                onClick={(e) => {
                  e.stopPropagation();
                  setHoveredOverlay(null);
                  if (imgEl.wasPhotoPlaceholder) {
                    updateElement({ ...imgEl, type: 'photoPlaceholder', src: null });
                    toast.success('Photo removed');
                  } else {
                    deleteElement(imgEl.id);
                    toast.success('Image deleted');
                  }
                }}
                className="bg-red-500 hover:bg-red-600 text-white rounded-full p-2 shadow-lg pointer-events-auto"
                whileHover={{ scale: 1.1 }} whileTap={{ scale: 0.95 }}
                title={imgEl.wasPhotoPlaceholder ? 'Remove photo' : 'Delete image'}
                data-testid={`delete-image-overlay-${imgEl.id}`}>
                <Trash2 className="h-5 w-5" />
              </motion.button>
              <motion.button
                onClick={(e) => {
                  e.stopPropagation();
                  setHoveredOverlay(null);
                  setSelectedId(imgEl.id);
                  if (availablePhotos.length > 0) {
                    setPhotoPickerTarget({ type: 'image', elementId: imgEl.id });
                    setShowPhotoPicker(true);
                  } else { fileInputRef.current?.click(); }
                }}
                className="bg-blue-500 hover:bg-blue-600 text-white rounded-full p-2 shadow-lg pointer-events-auto"
                whileHover={{ scale: 1.1 }} whileTap={{ scale: 0.95 }}
                title="Replace image" data-testid={`replace-image-overlay-${imgEl.id}`}>
                <RefreshCw className="h-5 w-5" />
              </motion.button>
            </div>
          </div>
        );
      })}
    </>
  );
};
